

/**
 * Cache entry interface
 */
export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  ttl: number;
  key: string;
}

/**
 * Cache options for decorators
 */
export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  key?: string; // Custom cache key
  maxSize?: number; // Maximum number of entries
  strategy?: 'memory' | 'redis' | 'file'; // Cache strategy
}

/**
 * Simple in-memory cache implementation
 */
export class MemoryCache {
  private cache = new Map<string, CacheEntry>();
  private maxSize: number;

  constructor(maxSize: number = 1000) {
    this.maxSize = maxSize;
  }

  /**
   * Get a value from cache
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check if expired
    if (Date.now() > entry.timestamp + entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Set a value in cache
   */
  set<T>(key: string, value: T, ttl: number = 60000): void {
    // Evict oldest entries if at capacity
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data: value,
      timestamp: Date.now(),
      ttl,
      key
    });
  }

  /**
   * Delete a value from cache
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; maxSize: number } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize
    };
  }
}

/**
 * Global cache instance
 */
const globalCache = new MemoryCache();

/**
 * Generate cache key from function name and arguments
 */
function generateCacheKey(fnName: string, args: any[]): string {
  const argsString = JSON.stringify(args);
  return `${fnName}:${Buffer.from(argsString).toString('base64')}`;
}

/**
 * Cache decorator for tool methods
 */
export function Cache(options: CacheOptions = {}) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;

    descriptor.value = async function (...args: any[]) {
      const cacheKey = options.key || generateCacheKey(propertyKey, args);
      const ttl = options.ttl || 60000; // Default 1 minute

      // Try to get from cache
      const cached = globalCache.get(cacheKey);
      if (cached !== null) {
        return cached;
      }

      // Execute original method
      const result = await originalMethod.apply(this, args);

      // Cache the result
      globalCache.set(cacheKey, result, ttl);

      return result;
    };

    return descriptor;
  };
}

/**
 * Cache decorator for resource methods
 */
export function CacheResource(options: CacheOptions = {}) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;

    descriptor.value = async function (...args: any[]) {
      const cacheKey = options.key || `${propertyKey}:resource`;
      const ttl = options.ttl || 300000; // Default 5 minutes for resources

      // Try to get from cache
      const cached = globalCache.get(cacheKey);
      if (cached !== null) {
        return cached;
      }

      // Execute original method
      const result = await originalMethod.apply(this, args);

      // Cache the result
      globalCache.set(cacheKey, result, ttl);

      return result;
    };

    return descriptor;
  };
}

/**
 * Cache invalidation decorator
 */
export function InvalidateCache(pattern: string | RegExp) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;

    descriptor.value = async function (...args: any[]) {
      // Execute original method
      const result = await originalMethod.apply(this, args);

      // Invalidate matching cache entries
      const keys = Array.from(globalCache['cache'].keys());
      const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
      
      for (const key of keys) {
        if (regex.test(key)) {
          globalCache.delete(key);
        }
      }

      return result;
    };

    return descriptor;
  };
}

/**
 * Cache utilities for manual cache management
 */
export class CacheUtils {
  /**
   * Get cache statistics
   */
  static getStats() {
    return globalCache.getStats();
  }

  /**
   * Clear all cache entries
   */
  static clear() {
    globalCache.clear();
  }

  /**
   * Invalidate cache entries matching pattern
   */
  static invalidate(pattern: string | RegExp): number {
    const keys = Array.from(globalCache['cache'].keys());
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    let invalidated = 0;

    for (const key of keys) {
      if (regex.test(key)) {
        globalCache.delete(key);
        invalidated++;
      }
    }

    return invalidated;
  }

  /**
   * Set cache entry manually
   */
  static set<T>(key: string, value: T, ttl: number = 60000): void {
    globalCache.set(key, value, ttl);
  }

  /**
   * Get cache entry manually
   */
  static get<T>(key: string): T | null {
    return globalCache.get<T>(key);
  }
}

/**
 * Example usage:
 * 
 * class MyTools {
 *   @Tool({ name: 'expensiveOperation', input: z.object({ data: z.string() }) })
 *   @Cache({ ttl: 300000 }) // Cache for 5 minutes
 *   async expensiveOperation({ data }: { data: string }, context: any) {
 *     // Expensive computation here
 *     return { result: 'processed' };
 *   }
 * 
 *   @Tool({ name: 'updateData', input: z.object({ data: z.string() }) })
 *   @InvalidateCache('expensiveOperation') // Invalidate related cache
 *   async updateData({ data }: { data: string }, context: any) {
 *     // Update data
 *     return { success: true };
 *   }
 * }
 */ 